<style scoped>
img {
  width: 60%;
}
code {
  display: block;
  white-space: pre;
  background-color: #f5f5f5;
  padding: 0.8rem;
  border-radius: 4px;
  font-size: 0.85em;
  overflow-x: auto;
}
.inline-code {
  display: inline;
  padding: 0.15rem 0.4rem;
  white-space: nowrap;
}
</style>

<template>
  <v-stepper
    v-model="step"
    style="height:100%;overflow-y:scroll;max-width:100%;margin-right:1rem;"
    class="elevation-0"
    alt-labels
  >
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1"
        >Generate Certificate</v-stepper-step
      >
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 2" step="2"
        >Send data via HTTP</v-stepper-step
      >
      <v-divider></v-divider>
      <v-stepper-step step="3">Send data via MQTT</v-stepper-step>
      <v-divider></v-divider>
    </v-stepper-header>

    <v-stepper-items class="subheading text-xs-left">
      <v-btn
        fab
        dark
        color="#FFD300"
        style="color:#002052;position:absolute;right:0;margin-top:1.5rem;z-index:10"
        @click="
          step++;
          step = step == 4 ? 1 : step;
        "
      >
        <v-icon dark>fast_forward</v-icon>
      </v-btn>
      <v-btn
        fab
        dark
        color="#FFD300"
        style="color:#002052;position:absolute;right:0;bottom:0;z-index:10"
        @click="
          step++;
          step = step == 4 ? 1 : step;
        "
      >
        <v-icon dark>fast_forward</v-icon>
      </v-btn>

      <!-- Step 1: Generate Certificate -->
      <v-stepper-content step="1">
        <v-card>
          <v-card-title style="background-color:#39a9dc">
            <v-icon class="mr-3" dark size="42">mdi-certificate</v-icon>
            <h2 class="title white--text font-weight-light">
              Generate ECDSA Certificate
            </h2>
          </v-card-title>
          <v-container>
            <v-layout row wrap>
              <v-flex xs12>
                <p>
                  NB-IoT devices authenticate via
                  <b>X.509 client certificates</b> using the
                  <b>ECDSA (EC_prime256v1)</b> algorithm. Generate a certificate
                  and register it on AWS IoT Core.
                </p>
                <v-alert :value="true" type="info" outline class="mb-3">
                  <div style="margin-bottom:0.5rem">
                    You can register an NB-IoT device in two ways:
                  </div>
                  <ul style="margin:0;padding-left:1.2rem;">
                    <li>
                      <b>Provide credentials manually</b>: generate (or reuse)
                      your own ECDSA private key and X.509 certificate, then
                      upload them during device provisioning.
                    </li>
                    <li>
                      <b>Use in-cloud credential generation</b>: let the
                      platform generate credentials in cloud during provisioning
                      and bind them to the new device automatically.
                    </li>
                  </ul>
                </v-alert>
                <h3 class="mb-2">1A. Manual credentials procedure</h3>
                <p>
                  Generate and manage credentials outside the platform, then
                  upload them during provisioning.
                </p>
                Generate ECDSA private key (prime256v1) and Certificate Signing
                Request
                <code>
                  openssl ecparam -name prime256v1 -genkey -noout -out
                  private-key.pem
                </code>
                <code>
                  openssl req -new -x509 -key private-key.pem -out
                  certificate.pem -days 365 -sha256
                </code>

                <h3 class="mt-3 mb-2">1B. In-cloud credentials procedure</h3>
                <p>
                  Let the platform generate the device credentials in cloud
                  during provisioning. No local OpenSSL generation is required
                  for this path.
                </p>

                <h3 class="mt-3 mb-2">
                  2A. Register device with manual credentials
                </h3>
                <p>
                  From the Asset Tracking dashboard, navigate to the
                  <b>Devices</b> section, select <b>NB-IoT</b> as the device
                  type, assign a unique ID and label, then upload the generated
                  certificate and private key (or related credential material
                  required by your provisioning flow).
                </p>

                <h3 class="mt-3 mb-2">
                  2B. Register device with in-cloud credentials
                </h3>
                <p>
                  From the same NB-IoT provisioning flow, choose the in-cloud
                  credential generation option, complete device metadata (ID and
                  label), and finalize registration so credentials are generated
                  and bound automatically.
                </p>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-stepper-content>

      <!-- Step 2: Send data via HTTP -->
      <v-stepper-content step="2">
        <v-card>
          <v-card-title style="background-color:#39a9dc">
            <v-icon class="mr-3" dark size="42">mdi-web</v-icon>
            <h2 class="title white--text font-weight-light">
              Send data via HTTP
            </h2>
          </v-card-title>
          <v-container>
            <v-layout row wrap>
              <v-flex xs12>
                <p>
                  Send telemetry data using an HTTP POST request. This method
                  requires an <b>API key</b> for authentication (create one from
                  <a v-on:click="$emit('routing', 'settings')">settings</a>).
                </p>
                <h3 class="mb-2">HTTP Request</h3>
                <ul>
                  <li><b>Method:</b> POST</li>
                  <li>
                    <b>URL:</b>
                    <code class="inline-code"
                      >https://{{ httpEndpointPrefix }}.click/{{
                        http.basePath
                      }}/telemetry</code
                    >
                  </li>
                  <li>
                    <b>Headers:</b>
                    <ul>
                      <li>
                        <code class="inline-code"
                          >Authorization: &lt;your-api-key&gt;</code
                        >
                      </li>
                      <li>
                        <code class="inline-code"
                          >Content-Type: application/json</code
                        >
                      </li>
                    </ul>
                  </li>
                </ul>

                <h3 class="mt-3 mb-2">Payload Format</h3>
                <code>
                  { "device_id": "&lt;your-device-id&gt;", "values": [ {"ts":
                  1745420400000, "t": "gnss", "v": {"lat": 45.0703, "lon":
                  7.6869, "alt": 250}}, {"ts": 1745420400000, "t": "temp", "v":
                  22.5}, {"ts": 1745420400000, "t": "hum", "v": 60}, {"ts":
                  1745420400000, "t": "pres", "v": 1013.26}, {"ts":
                  1745420400000, "t": "acc", "v": {"x": 0.1, "y": 0.1, "z":
                  0.1}}, {"ts": 1745420400000, "t": "gyr", "v": {"x": 0.1, "y":
                  0.1, "z": 0.1}}, {"ts": 1745420400000, "t": "mag", "v": {"x":
                  0.1, "y": 0.1, "z": 0.1}} ] }
                </code>

                <h3 class="mt-3 mb-2">Telemetry Types</h3>
                <v-simple-table v-if="false"></v-simple-table>
                <table
                  class="mt-1"
                  style="width:100%;border-collapse:collapse;"
                >
                  <thead>
                    <tr style="background-color:#f5f5f5;">
                      <th
                        style="padding:0.5rem;border:1px solid #ddd;text-align:left;"
                      >
                        Type (<code class="inline-code">t</code>)
                      </th>
                      <th
                        style="padding:0.5rem;border:1px solid #ddd;text-align:left;"
                      >
                        Description
                      </th>
                      <th
                        style="padding:0.5rem;border:1px solid #ddd;text-align:left;"
                      >
                        Value (<code class="inline-code">v</code>) format
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">gnss</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        GNSS position
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">{"lat", "lon", "alt"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">temp</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Temperature (°C)
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Number
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">hum</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Humidity (%)
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Number
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">pres</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Pressure (hPa)
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Number
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">acc</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Accelerometer
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">{"x", "y", "z"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">gyr</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Gyroscope
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">{"x", "y", "z"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">mag</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Magnetometer
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">{"x", "y", "z"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        <code class="inline-code">evt</code>
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Event
                      </td>
                      <td style="padding:0.5rem;border:1px solid #ddd;">
                        Object
                      </td>
                    </tr>
                  </tbody>
                </table>

                <v-alert :value="true" type="info" class="mt-3" outline>
                  <b>ts</b> is the Unix timestamp in milliseconds. Each entry in
                  <b>values</b> contains a timestamp (<code class="inline-code"
                    >ts</code
                  >), telemetry type (<code class="inline-code">t</code>), and
                  value (<code class="inline-code">v</code>).
                </v-alert>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-stepper-content>

      <!-- Step 3: Send data via MQTT -->
      <v-stepper-content step="3">
        <v-card>
          <v-card-title style="background-color:#39a9dc">
            <v-icon dark size="42" class="mr-3">mdi-access-point</v-icon>
            <h2 class="title white--text font-weight-light">
              Send data via MQTT
            </h2>
          </v-card-title>
          <v-container>
            <v-layout row wrap>
              <v-flex xs12>
                <p>
                  NB-IoT devices can publish telemetry data directly via
                  <b>MQTT</b> using the ECDSA client certificate generated in
                  Step 1.
                </p>

                <h3 class="mb-2">MQTT Connection</h3>
                <ul>
                  <li>
                    <b>Endpoint:</b>
                    <code class="inline-code"
                      >mqtt.{{ mqttEndpointPrefix }}.click</code
                    >
                    (ECDSA custom domain)
                  </li>
                  <li>
                    <b>Port:</b> <code class="inline-code">8883</code> (TLS)
                  </li>
                  <li>
                    <b>Client ID:</b>
                    <code class="inline-code">&lt;your-device-id&gt;</code>
                  </li>
                  <li>
                    <b>Topic:</b>
                    <code class="inline-code"
                      >atr/v1/&lt;your-device-id&gt;/data</code
                    >
                  </li>
                  <li>
                    <b>TLS certificates:</b>
                    <ul>
                      <li>
                        <code class="inline-code">certificate.pem</code> —
                        device certificate
                      </li>
                      <li>
                        <code class="inline-code">private-key.pem</code> —
                        device private key
                      </li>
                      <li>
                        <code class="inline-code">AmazonRootCA1.pem</code> —
                        Amazon Root CA
                      </li>
                    </ul>
                  </li>
                </ul>

                <h3 class="mt-3 mb-2">Example: Publish with Mosquitto</h3>
                <code>
                  DEVICE_ID="your-device-id" mosquitto_pub \ --cafile
                  AmazonRootCA1.pem \ --cert certificate.pem \ --key
                  private-key.pem \ -h mqtt.{{ mqttEndpointPrefix }}.click \ -p
                  8883 \ -t "atr/v1/$DEVICE_ID/data" \ -m '{ "device_id":
                  "'$DEVICE_ID'", "values": [ {"ts": 1745420400000, "t": "gnss",
                  "v": {"lat": 45.0703, "lon": 7.6869, "alt": 250}}, {"ts":
                  1745420400000, "t": "temp", "v": 22.5}, {"ts": 1745420400000,
                  "t": "hum", "v": 60}, {"ts": 1745420400000, "t": "pres", "v":
                  1013.26} ] }'
                </code>

                <h3 class="mt-3 mb-2">Example: Python MQTT client</h3>
                <code>
                  import ssl, json import paho.mqtt.client as mqtt DEVICE_ID =
                  "your-device-id" ENDPOINT = "mqtt.{{
                    mqttEndpointPrefix
                  }}.click" TOPIC = f"atr/v1/{DEVICE_ID}/data" client =
                  mqtt.Client(client_id=DEVICE_ID) client.tls_set(
                  ca_certs="AmazonRootCA1.pem", certfile="certificate.pem",
                  keyfile="private-key.pem", tls_version=ssl.PROTOCOL_TLSv1_2 )
                  client.connect(ENDPOINT, port=8883) payload = { "device_id":
                  DEVICE_ID, "values": [ {"ts": 1745420400000, "t": "temp", "v":
                  22.5}, {"ts": 1745420400000, "t": "hum", "v": 60} ] }
                  client.publish(TOPIC, json.dumps(payload), qos=1)
                  client.disconnect()
                </code>

                <v-alert :value="true" type="info" class="mt-3" outline>
                  The payload format is <b>identical</b> for both HTTP and MQTT.
                  No API key is needed for MQTT — authentication is handled by
                  the X.509 client certificate.
                </v-alert>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import {mapState} from "vuex";
export default {
  computed: {
    ...mapState({
      http: (state) => state.metadata.backendApi.http[state.metadata.env],
      mqtt: (state) => state.metadata.backendApi.mqtt[state.metadata.env],
    }),
    httpEndpointPrefix() {
      if (this.http && this.http.host) {
        return this.http.host.split(".")[0];
      }
      return "&lt;endpoint-prefix&gt;";
    },
    mqttEndpointPrefix() {
      if (this.mqtt && this.mqtt.host) {
        return this.mqtt.host.split(".")[0];
      }
      return "&lt;endpoint-prefix&gt;";
    },
  },
  data() {
    return {
      step: 1,
    };
  },
};
</script>
